import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Radio, AlertTriangle, Quote, Youtube, ExternalLink, Bell, Volume2, CalendarClock } from "lucide-react";

function tagStyle(tag) {
  return tag === "alert" ? "bg-red-100 text-red-800 border-red-300" :
         tag === "quote" ? "bg-amber-100 text-amber-800 border-amber-300" :
         "bg-blue-100 text-blue-800 border-blue-300";
}

function useCountdown(targetIso) {
  const [remaining, setRemaining] = useState(() => targetIso ? new Date(targetIso).getTime() - Date.now() : null);
  useEffect(() => {
    if (!targetIso) { setRemaining(null); return; }
    const tick = () => setRemaining(new Date(targetIso).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (remaining == null || remaining < 0) return null;
  const s = Math.floor(remaining / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    mins: Math.floor((s % 3600) / 60),
    secs: s % 60,
  };
}

export default function LiveBlogPage() {
  const [lb, setLb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveYT, setLiveYT] = useState(null);
  const [muted, setMuted] = useState(true);

  const load = () => Promise.all([
    api.get("/live-blog/active").then((r) => setLb(r.data)).catch(() => setLb(null)),
    api.get("/youtube/live").then((r) => setLiveYT(r.data)).catch(() => setLiveYT(null)),
  ]).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const isLive = !!liveYT?.is_live && !!liveYT?.video_id;
  const upcoming = liveYT?.upcoming;
  const countdown = useCountdown(upcoming?.scheduled_start_time);
  const fallbackVid = liveYT?.fallback?.video_id;
  const showVideoId = isLive ? liveYT.video_id : fallbackVid;
  const embedUrl = showVideoId
    ? `https://www.youtube.com/embed/${showVideoId}?autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1${isLive ? "" : "&controls=1"}`
    : null;

  return (
    <>
      <PublicHeader />

      <section className={`${isLive ? "bg-gradient-to-r from-red-700 via-red-600 to-rose-700" : "bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900"} text-white relative overflow-hidden`}>
        <div className="max-w-6xl mx-auto px-4 py-6 relative z-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {isLive ? (
              <>
                <span className="inline-flex items-center gap-1.5 bg-white text-red-700 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest">
                  <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"/>LIVE NOW
                </span>
                <span className="text-[10px] uppercase tracking-widest text-amber-200 font-black">INDIAN-NEWS-21 · Live TV</span>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 bg-slate-700 text-slate-200 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest">
                  <Radio className="w-3 h-3"/>अभी Live नहीं
                </span>
                <span className="text-[10px] uppercase tracking-widest text-amber-300 font-black">INDIAN-NEWS-21 · Live TV</span>
              </>
            )}
          </div>
          <h1 className="font-devnagri-heading text-2xl md:text-4xl font-black leading-tight" data-testid="live-tv-title">
            {isLive ? (liveYT.title || "Live प्रसारण चल रहा है") : "Live TV · भारत की आवाज़"}
          </h1>
          <p className="text-sm md:text-base opacity-90 mt-2">
            {isLive
              ? "अभी हमारा प्रसारण live है — नीचे देखिए। जब live नहीं होगा तब यहाँ latest video दिखेगा।"
              : "अभी हमारा प्रसारण live नहीं है। जब भी live आएगा, यहाँ automatic दिखने लगेगा। तब तक हमारा latest video देखिए।"}
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="live-tv-page">
        {/* Upcoming countdown */}
        {!isLive && upcoming?.scheduled_start_time && countdown && (
          <div className="mb-4 rounded-2xl overflow-hidden bg-gradient-to-r from-amber-500 via-orange-600 to-rose-600 text-white p-5 shadow-lg" data-testid="live-upcoming-countdown">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-5 h-5 text-amber-100"/>
              <div className="text-[10px] uppercase tracking-widest font-black text-amber-100">आने वाला Live प्रसारण</div>
            </div>
            <div className="font-devnagri-heading text-xl font-black leading-tight mb-3 line-clamp-2">{upcoming.title || "अगला Live आ रहा है"}</div>
            <div className="grid grid-cols-4 gap-2 max-w-md">
              {[
                { v: countdown.days, l: "दिन" },
                { v: countdown.hours, l: "घंटे" },
                { v: countdown.mins, l: "मिनट" },
                { v: countdown.secs, l: "सेकंड" },
              ].map((c, i) => (
                <div key={i} className="rounded-lg bg-black/30 backdrop-blur px-2 py-2 text-center">
                  <div className="font-devnagri-heading text-2xl font-black tabular-nums leading-none">{String(c.v).padStart(2, "0")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-100 font-bold mt-1">{c.l}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-amber-100 mt-3">🔔 Subscribe करके bell दबाइए ताकि live आते ही आपको notification मिले</div>
          </div>
        )}

        {/* YouTube embed */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-800 bg-black" data-testid="live-tv-player">
          {showVideoId ? (
            <>
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={embedUrl}
                  title={isLive ? "INDIAN-NEWS-21 Live" : "INDIAN-NEWS-21 Latest"}
                  frameBorder="0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid="live-tv-iframe"
                />
              </div>
              {isLive && (
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-red-600 text-white rounded px-2.5 py-1 text-[10px] font-black uppercase tracking-widest shadow-lg">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>LIVE
                </div>
              )}
              {muted && (
                <button
                  onClick={() => setMuted(false)}
                  className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-black/70 hover:bg-black/90 text-white rounded-full px-3 py-1.5 text-xs font-bold backdrop-blur"
                  data-testid="live-tv-unmute"
                >
                  <Volume2 className="w-3.5 h-3.5"/>Audio चालू करो
                </button>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center bg-gradient-to-br from-slate-900 to-black text-white">
              <Youtube className="w-16 h-16 text-red-600 mb-4"/>
              <div className="font-devnagri-heading text-xl font-black">अभी कोई video नहीं मिली</div>
              <div className="text-sm text-slate-400 mt-1">YouTube API से जुड़ने में समस्या — कुछ देर में पुनः प्रयास करें</div>
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className={`rounded-xl p-4 border-2 ${isLive ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">Status</div>
            {isLive ? (
              <div className="font-devnagri-heading text-lg font-black text-red-700 mt-1">🔴 अभी LIVE · देखिए इंडियन न्यूज़-21 का ताज़ा प्रसारण</div>
            ) : (
              <>
                <div className="font-devnagri-heading text-lg font-black text-slate-800 mt-1">अभी Live नहीं</div>
                {liveYT?.fallback?.title && <div className="text-xs text-slate-600 mt-1 line-clamp-2">📺 दिखा रहा है: <b>{liveYT.fallback.title}</b></div>}
              </>
            )}
          </div>
          <a
            href={liveYT?.channel_url || "https://www.youtube.com/@cgIndiannews21?sub_confirmation=1"}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl p-4 border-2 border-red-300 bg-gradient-to-r from-red-600 to-rose-700 text-white flex items-center justify-between hover:from-red-700 hover:to-rose-800 transition group"
            data-testid="live-tv-subscribe"
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-red-100">YouTube पर</div>
              <div className="font-black text-lg leading-tight">Subscribe · Bell 🔔 दबाओ</div>
              <div className="text-[11px] text-red-100">हर live प्रसारण की तुरंत notification मिलेगी</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition">
              <Bell className="w-6 h-6"/>
            </div>
          </a>
        </div>

        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-5 h-5 text-red-600"/>
            <h2 className="font-devnagri-heading text-2xl font-black text-[#0F172A]">Live Blog · लाइव अपडेट्स</h2>
          </div>
          {loading ? <div className="text-slate-500">लोड हो रहा है…</div> :
           !lb ? <div className="text-slate-500 italic text-center py-8 bg-slate-50 rounded-lg border border-slate-200">अभी कोई live text-updates नहीं। जब भी कोई breaking event होगा, यहाँ live updates आएँगे।</div> :
           <div>
             <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
               <div className="font-bold text-[#0F172A]">{lb.title}</div>
               {lb.subtitle && <div className="text-xs text-slate-600 mt-1">{lb.subtitle}</div>}
             </div>
             <div className="space-y-4 border-l-2 border-slate-200 pl-6">
               {lb.entries.map((e) => (
                 <div key={e.eid} className="relative" data-testid={`lb-entry-${e.eid}`}>
                   <div className="absolute -left-[30px] top-1 w-4 h-4 rounded-full bg-red-500 ring-4 ring-red-100"></div>
                   <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border font-bold ${tagStyle(e.tag)}`}>
                     {e.tag === "alert" ? <AlertTriangle className="w-3 h-3"/> : e.tag === "quote" ? <Quote className="w-3 h-3"/> : null}
                     {e.tag}
                   </div>
                   <div className="text-[10px] text-slate-400 ml-2 inline">{new Date(e.at).toLocaleString("hi-IN")}</div>
                   <div className="mt-1.5 text-slate-800 leading-relaxed whitespace-pre-wrap">{e.content}</div>
                 </div>
               ))}
             </div>
           </div>
          }
        </div>

        <div className="mt-8 text-center text-xs text-slate-500">
          <a href={liveYT?.channel_url || "https://www.youtube.com/@cgIndiannews21"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-red-600" data-testid="live-tv-channel-link">
            <ExternalLink className="w-3.5 h-3.5"/>YouTube channel <span className="font-bold">@cgIndiannews21</span>
          </a>
        </div>
      </main>
      <PublicFooter />
    </>
  );
}
